import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import * as api from "./api";
import { toast } from "@/components/ui/toast";

/*
 * useGetAssets Query
 *
 * Fetches all assets for a given study.
 * queryKey: ["assets", projectId, studyId]
 */
export const useGetAssets = (projectId: string, studyId: string) => {
  return useQuery({
    queryKey: ["assets", projectId, studyId],
    queryFn: () => api.getAssets(projectId, studyId),
  });
};

/*
 * useCreateAsset Mutation
 *
 * Creates a new asset.
 */
export const useCreateAsset = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, studyId, values }: { projectId: string; studyId: string; values: api.CreateAssetReq }) => api.createAsset(projectId, studyId, values),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["assets", vars.projectId, vars.studyId] });
      toast({
        title: "Asset successfully created",
        type: "success",
      });
    },
    onError: () => {
      toast({
        title: "Failed to create asset",
        type: "error",
      });
    },
  });
};

/*
 * useUpdateAsset Mutation
 *
 * Updates an existing asset.
 */
export const useUpdateAsset = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: api.updateAsset,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["assets", vars.projectId, vars.studyId] });
      toast({
        title: "Asset successfully updated",
        type: "success",
      });
    },
    onError: () => {
      toast({
        title: "Failed to update asset",
        type: "error",
      });
    },
  });
};

/*
 * useDeleteAsset Mutation
 *
 * Deletes an existing asset.
 */
export const useDeleteAsset = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: api.deleteAsset,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["assets", vars.projectId, vars.studyId] });
      toast({
        title: "Asset successfully deleted",
        type: "success",
      });
    },
    onError: () => {
      toast({
        title: "Failed to delete asset",
        type: "error",
      });
    },
  });
};
